import { TeamCollection } from "./TeamCollection"
import { TeamRequest } from "./TeamRequest"

/**
 * Compares two lists of team collections by their top-level metadata only
 * (id, title, data) — ignores children/requests. Used to detect whether a
 * poll actually returned different data before touching reactive state.
 */
export const teamCollectionsShallowEqual = (
  a: TeamCollection[],
  b: TeamCollection[]
): boolean => {
  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (x.id !== y.id || x.title !== y.title || x.data !== y.data) {
      return false
    }
  }

  return true
}

/**
 * Compares two lists of team requests by id, title, and serialized request
 * body. Used to detect whether a poll actually returned different data
 * before touching reactive state.
 */
export const teamRequestsShallowEqual = (
  a: TeamRequest[],
  b: TeamRequest[]
): boolean => {
  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (
      x.id !== y.id ||
      x.title !== y.title ||
      JSON.stringify(x.request) !== JSON.stringify(y.request)
    ) {
      return false
    }
  }

  return true
}

/**
 * Applies the latest server fields without discarding children and requests
 * that were already loaded for an expanded collection.
 *
 * If the incoming root list is identical (by teamCollectionsShallowEqual) to
 * the currently loaded list, the original `current` reference is returned
 * unchanged so callers can skip reactive updates entirely (used by the
 * polling refresh path to avoid re-rendering the tree when nothing changed).
 */
export const reconcileTeamCollections = (
  current: TeamCollection[],
  incoming: TeamCollection[]
): TeamCollection[] => {
  if (teamCollectionsShallowEqual(current, incoming)) return current

  const currentByID = new Map(
    current.map((collection) => [collection.id, collection])
  )

  return incoming.map((collection) => {
    const loadedCollection = currentByID.get(collection.id)

    if (!loadedCollection) return collection

    return {
      ...loadedCollection,
      ...collection,
      children: loadedCollection.children,
      requests: loadedCollection.requests,
    }
  })
}

export const getExpandedCollectionIDsByDepth = (tree: TeamCollection[]) => {
  const levels: string[][] = []

  const visit = (collections: TeamCollection[], depth: number) => {
    for (const collection of collections) {
      if (collection.children === null) continue

      levels[depth] ??= []
      levels[depth].push(collection.id)
      visit(collection.children, depth + 1)
    }
  }

  visit(tree, 0)
  return levels
}
